import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "../utils";
import {
  AccountAndAsset,
  AccountAndAssetUpdateRequest,
  AccountAndAssetCreateRequest,
} from "../../../../api/src/types";

interface BasePathParams {
  userId: string;
  docId: string;
}

// linter takes away this const

// eslint-disable-next-line
const accountsAndAssetsTagType = "accountsAndAssets" as const;

export const accountsAndAssetsApi = createApi({
  reducerPath: "accountsAndAssetsApi",
  baseQuery,
  tagTypes: [accountsAndAssetsTagType],
  endpoints: (builder) => ({
    getAccountAndAsset: builder.query<
      AccountAndAsset,
      BasePathParams & { id: string }
    >({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/assets/${id}`,
        method: "GET",
      }),

      providesTags: (_, __, query) => [
        { type: accountsAndAssetsTagType, id: query.id },
      ],
    }),
    getAccountsAndAssetsByUserId: builder.query<
      AccountAndAsset[],
      BasePathParams
    >({
      query: ({ userId, docId }) => {
        return `users/${userId}/documents/${docId}/assets`;
      },
      merge: (currentCache, newItems) => {
        const itemMap = new Map(currentCache.map((item) => [item.id, item]));
        newItems.forEach((item) => itemMap.set(item.id, item));
        return Array.from(itemMap.values());
      },

      providesTags: (result) => [
        ...(result?.map((accountAndAsset) => ({
          type: accountsAndAssetsTagType,
          id: accountAndAsset.id,
        })) ?? []),
        { type: accountsAndAssetsTagType, id: "LIST" },
      ],
    }),

    updateAccountAndAsset: builder.mutation<
      AccountAndAsset,
      BasePathParams & { id: string; data: AccountAndAssetUpdateRequest }
    >({
      query: ({ userId, docId, id, data }) => ({
        url: `users/${userId}/documents/${docId}/assets/${id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (_, __, { id }) => [
        { type: accountsAndAssetsTagType, id },
      ],
    }),

    createAccountAndAsset: builder.mutation<
      AccountAndAsset,
      BasePathParams & { data: AccountAndAssetCreateRequest }
    >({
      query: ({ userId, docId, data }) => ({
        url: `users/${userId}/documents/${docId}/assets`,
        method: "POST",
        body: data,
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: newItem } = await queryFulfilled;
          if (newItem.userId && newItem.id && newItem.documentId) {
            dispatch(
              accountsAndAssetsApi.util.updateQueryData(
                "getAccountsAndAssetsByUserId",
                { userId: newItem.userId, docId: newItem.documentId },
                (draft: AccountAndAsset[]) => {
                  draft.push(newItem);
                },
              ),
            );
          }
        } catch (error) {
          console.log("rtk query createAccountAndAsset error", error);
        }
      },
    }),
    deleteAccountAndAsset: builder.mutation<
      AccountAndAsset,
      BasePathParams & { id: string }
    >({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/assets/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(
        { userId, docId, id },
        { dispatch, queryFulfilled },
      ) {
        await queryFulfilled;
        dispatch(
          accountsAndAssetsApi.util.updateQueryData(
            "getAccountsAndAssetsByUserId",
            { userId, docId },
            (draft: AccountAndAsset[]) => {
              return draft.filter((item) => item.id !== id);
            },
          ),
        );
      },
    }),
  }),
});

export const {
  useGetAccountAndAssetQuery,
  useGetAccountsAndAssetsByUserIdQuery,
  useUpdateAccountAndAssetMutation,
  useCreateAccountAndAssetMutation,
  useDeleteAccountAndAssetMutation,
} = accountsAndAssetsApi;
